import React from 'react'

import { convertUTCtoMSK, formatSum } from '../../../../../../utils'
import { SmartTooltip } from '../../../../../../components/SmartTooltip/SmartTooltip'
import { DepositTokenLink } from '../../../../../common/DepositTokenLink'
import { TruncatedValue } from '../../../../../common/TruncatedValue'
import { AUTO_DISPUTE_SITUATION_LABEL } from './consts'
import { DisputeRecord } from './response.types'

/** Нет поля — строка от бэкенда предыдущего релиза, это спор о пополнении. */
export const isPayoutDispute = (d: DisputeRecord): boolean => d.type === 'PAYOUT_DISPUTE'

export function renderDisputeType(d: DisputeRecord): React.ReactNode {
  return isPayoutDispute(d) ? 'По выплате' : 'По депозиту'
}

/**
 * Виновники спора о выплате: ID транзакции у мерчанта — по нему пополнение узнают в переписке, —
 * под ним токен ссылкой на карточку. У спора о пополнении виновников нет.
 */
export function renderCulprits(d: DisputeRecord): React.ReactNode {
  const culprits = d.culprits ?? []
  if (!culprits.length) {
    return <span className='text-muted'>—</span>
  }

  return (
    <div className='d-flex flex-column gap-1'>
      {culprits.map((culprit) => (
        <div key={culprit.token}>
          <TruncatedValue value={culprit.merchantTransactionId} className='text-monospace' empty='—' />
          <div className='fs-8'>
            <DepositTokenLink token={culprit.token} />
          </div>
        </div>
      ))}
    </div>
  )
}

const COMMENT_MAX_LENGTH = 60

/**
 * Длинный комментарий распирал колонку и ломал ряд кнопок. Обрезаем, полный текст — в подсказке.
 */
export function renderComment(comment: string | null): React.ReactNode {
  if (!comment) {
    return '—'
  }

  if (comment.length <= COMMENT_MAX_LENGTH) {
    return comment
  }

  return (
    <SmartTooltip content={<div style={{ whiteSpace: 'pre-wrap' }}>{comment}</div>}>
      <span style={{ cursor: 'help' }}>{`${comment.slice(0, COMMENT_MAX_LENGTH)}…`}</span>
    </SmartTooltip>
  )
}

/**
 * У тикета в проверке чеков amount ещё пуст — показываем, что клиент заказал, и сумму к оплате,
 * если мэтчинг её скорректировал: именно расхождение обычно и есть предмет спора.
 *
 * Живёт отдельно от таблицы журнала: то же правило показывает вкладка «Диспуты» на карточке
 * клиента (CPAYMENT-1732), и расхождение двух копий читалось бы как разные суммы одного диспута.
 */
export function renderAmount(d: DisputeRecord): React.ReactNode {
  if (d.amount !== null) {
    return formatSum(d.amount)
  }

  const requested = d.externalUserApproximateAmount
  if (d.requisiteAmount !== null && d.requisiteAmount !== requested) {
    return (
      <span>
        {requested !== null ? formatSum(requested) : '—'}
        <span className='text-muted'> → </span>
        <span className='fw-bold'>{formatSum(d.requisiteAmount)}</span>
      </span>
    )
  }

  return requested !== null ? formatSum(requested) : '—'
}

/**
 * Кто закрыл спор.
 *
 * Исполнителя заполняет только решение сотрудника, поэтому закрытый спор без исполнителя закрыла
 * система — по истечении срока жизни спора. Отдельного статуса у такого исхода нет: он и по сути
 * тот же отказ, поэтому «кто» — единственное, чем он отличается, и различие должно читаться прямо
 * в журнале. У открытого спора исполнителя ещё нет, и прочерк там означает ровно это.
 */
export function renderResolvedBy(d: DisputeRecord): React.ReactNode {
  if (d.resolvedBy) {
    return d.resolvedBy
  }

  if (d.status === 'OPEN') {
    return '—'
  }

  // <em>Система</em> — та же запись, что в журнале правок реквизита: исполнителя нет, и курсив
  // отличает её от uuid сотрудника, не притворяясь ссылкой на кого-то.
  return <em>Система</em>
}

/**
 * Who opened the dispute. Only a dispute the system opened has no author; it is written the same
 * way as «Кто закрыл» for the system.
 */
export function renderCreatedBy(d: DisputeRecord): React.ReactNode {
  return d.createdBy ? d.createdBy : <em>Система</em>
}

/** Why the system opened the dispute; a dash for a dispute opened by an employee. */
export function renderAutoSituation(d: DisputeRecord): React.ReactNode {
  if (!d.autoSituation) {
    return '—'
  }

  return AUTO_DISPUTE_SITUATION_LABEL[d.autoSituation] ?? d.autoSituation
}

/**
 * Когда спорное пополнение отпустит выплату (CPAYMENT-1857).
 *
 * Пока спор открыт, выплата занята этим пополнением — его суммой и слотом чанка, — и это состояние
 * оператор обязан видеть в журнале, а не только внутри окна освобождения: от него зависит, ждать
 * или отпускать руками.
 *
 * Четыре состояния и все различимы: спор закрыт — вопроса нет; момент не задан — держим бессрочно;
 * момент впереди — сколько осталось; момент прошёл — выплата уже свободна, а строка журнала
 * остаётся до решения по спору.
 */
export function renderChunkHold(d: DisputeRecord): React.ReactNode {
  // Спор о выплате выплату не держит — освобождать нечего.
  if (d.status !== 'OPEN' || isPayoutDispute(d)) {
    return <span className='text-muted'>—</span>
  }

  if (!d.chunkHeldUntil) {
    return <span className='badge badge-light-warning'>бессрочно</span>
  }

  const minutes = Math.round((new Date(d.chunkHeldUntil).getTime() - Date.now()) / 60000)

  if (Number.isNaN(minutes)) {
    return <span className='text-muted'>—</span>
  }

  if (minutes < 1) {
    return <span className='text-muted'>освобождена</span>
  }

  return (
    <SmartTooltip content={<div>{convertUTCtoMSK(d.chunkHeldUntil)} МСК</div>}>
      <span style={{ cursor: 'help' }}>через {minutes} мин</span>
    </SmartTooltip>
  )
}
